import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  confirmSignIn,
  type SignInOutput,
} from 'aws-amplify/auth';

/**
 * Configure AWS Amplify with Cognito User Pool settings.
 * Values come from environment variables set at build/deploy time.
 */
export function configureAmplify(): void {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
        userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
      },
    },
  });
}

/**
 * Sign in with email and password.
 * Clears any stale session before attempting sign-in.
 */
export async function login(email: string, password: string): Promise<SignInOutput> {
  // Clear any stale session before attempting sign-in
  try {
    await signOut();
  } catch {
    // Non-fatal: proceed with sign-in even if sign-out fails
  }
  return signIn({ username: email, password });
}

/**
 * Sign out the current user and clear stored tokens.
 */
export async function logout(): Promise<void> {
  await signOut();
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiry');
}

/**
 * Get the current ID token, refreshing if needed.
 * The ID token contains user claims (email, name, custom:role, cognito:groups)
 * and is required by the API Gateway Cognito authorizer.
 * Returns null if no valid session exists.
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const idToken = session.tokens?.idToken?.toString() ?? null;
    if (idToken) {
      localStorage.setItem('idToken', idToken);
    }
    return idToken;
  } catch {
    return null;
  }
}

/**
 * Get the current access token, refreshing if needed.
 * Returns null if no valid session exists.
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const token = session.tokens?.accessToken?.toString() ?? null;
    if (token) {
      localStorage.setItem('authToken', token);
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        localStorage.setItem('idToken', idToken);
      }
      // Store expiry (tokens typically last 1 hour)
      const expiry = Date.now() + 3600 * 1000;
      localStorage.setItem('tokenExpiry', String(expiry));
    }
    return token;
  } catch {
    return null;
  }
}

/**
 * Check whether a valid session exists.
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the user's role from the Cognito ID token's cognito:groups claim.
 * Returns 'admin' | 'event' | 'user', defaulting to 'user' if not in any special group.
 */
export async function getUserRole(): Promise<'user' | 'admin' | 'event'> {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const idToken = session.tokens?.idToken;
    if (idToken) {
      const payload = idToken.payload as Record<string, unknown>;
      // Check cognito:groups - can be array or comma-separated string
      const groupsClaim = payload['cognito:groups'];
      let groups: string[] = [];
      if (Array.isArray(groupsClaim)) {
        groups = groupsClaim as string[];
      } else if (typeof groupsClaim === 'string') {
        groups = groupsClaim.split(',').map(g => g.trim()).filter(Boolean);
      }
      // Priority: admin > event > user
      if (groups.includes('admin')) return 'admin';
      if (groups.includes('event')) return 'event';
    }
    return 'user';
  } catch {
    return 'user';
  }
}

/**
 * Get the email address from the current session's ID token.
 * Returns null if no valid session or no email claim exists.
 */
export async function getSessionEmail(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const idToken = session.tokens?.idToken;
    if (idToken) {
      const payload = idToken.payload as Record<string, unknown>;
      return (payload['email'] as string) ?? null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Get the current authenticated user info including role from Cognito token.
 */
export async function getAuthUser(): Promise<{ userId: string; username: string; role: 'user' | 'admin' | 'event' } | null> {
  try {
    const user = await getCurrentUser();
    const role = await getUserRole();
    // Use email from ID token as display name instead of Cognito sub UUID
    const email = await getSessionEmail();
    return { userId: user.userId, username: email ?? user.username, role };
  } catch {
    return null;
  }
}


/**
 * Initiate forgot password flow — sends verification code to user's email.
 */
export async function forgotPassword(email: string): Promise<void> {
  await resetPassword({ username: email });
}

/**
 * Confirm forgot password — set new password with verification code.
 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}


/**
 * Complete a forced password change challenge (NEW_PASSWORD_REQUIRED).
 * Called after signIn returns nextStep CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED.
 * Must include required user attributes that weren't set during admin-create-user.
 */
export async function completeNewPassword(newPassword: string, name: string): Promise<SignInOutput> {
  return confirmSignIn({
    challengeResponse: newPassword,
    options: {
      userAttributes: { name },
    },
  });
}
